// import React, { useMemo } from "react";
// import ReactApexChart from "react-apexcharts";

// const ApexChart = ({ data, location }) => {
//   const { series, categories, options } = useMemo(() => {
//     if (!Array.isArray(data) || !data.length)
//       return { series: [], categories: [], options: {} };

//     const filteredData = data.filter((item) =>
//       location ? item.name.trim() === location : true
//     );

//     const allDates = [
//       ...new Set(
//         filteredData.flatMap((loc) => [
//           ...(loc.graph?.map((entry) => entry.ins_date) || []),
//           ...(loc.graph_accepted?.map((entry) => entry.ins_date) || []),
//           ...(loc.graph_rejected?.map((entry) => entry.ins_date) || []),
//         ])
//       ),
//     ].sort((a, b) => new Date(a) - new Date(b));

//     const seriesData = [
//       ...filteredData.map((loc) => ({
//         name: `${loc.name.trim()} Total`,
//         data: allDates.map((date) => {
//           const entry = loc.graph?.find((item) => item.ins_date === date);
//           return entry ? entry.count : 0;
//         }),
//       })),
//       ...filteredData.map((loc) => ({
//         name: `${loc.name.trim()} Accepted`,
//         data: allDates.map((date) => {
//           const entry = loc.graph_accepted?.find(
//             (item) => item.ins_date === date
//           );
//           return entry ? entry.count : 0;
//         }),
//       })),
//       ...filteredData.map((loc) => ({
//         name: `${loc.name.trim()} Rejected`,
//         data: allDates.map((date) => {
//           const entry = loc.graph_rejected?.find(
//             (item) => item.ins_date === date
//           );
//           return entry ? entry.count : 0;
//         }),
//       })),
//     ];

//     const formatDate = (dateString) => {
//       const date = new Date(dateString);
//       const month = String(date.getMonth() + 1).padStart(2, "0");
//       const day = String(date.getDate()).padStart(2, "0");
//       const year = date.getFullYear();
//       return `${month}/${day}/${year}`;
//     };

//     const options = {
//       chart: {
//         id: "dynamic-lines",
//         height: 350,
//         type: "line",
//         animations: { enabled: false },
//         toolbar: { show: true },
//         zoom: { enabled: false },
//       },
//       dataLabels: { enabled: false },
//       stroke: {
//         curve: "smooth",
//         width: 3,
//       },
//       markers: { size: 5 },
//       xaxis: {
//         categories: allDates.map(formatDate),
//         type: "datetime",
//         title: { text: "" },
//         labels: { rotate: -48, rotateAlways: true },
//       },
//       yaxis: {
//         title: { text: "Weld Counts" },
//       },
//       legend: {
//         show: true,
//         position: "bottom",
//         horizontalAlign: "center",
//       },
//       tooltip: {
//         x: {
//           formatter: function (val) {
//             return formatDate(categories[val - 1]);
//           },
//         },
//       },
//       colors: ["#008FFB", "#00E396", "#FF4560"],
//     };

//     console.log("All Dates:", allDates);
//     console.log("Formatted Dates:", allDates.map(formatDate));

//     return { series: seriesData, categories: allDates, options };
//   }, [data, location]);

//   return (
//     <div className="w-full">
//       <ReactApexChart
//         options={options}
//         series={series}
//         type="line"
//         height={450}
//       />
//     </div>
//   );
// };

// export default ApexChart;

import React, { useMemo, useRef, useEffect, useState } from "react";

const ApexChart = ({ data, location, inspectionProject }) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 450 });
  const PADDING = 60;
  const CHART_WIDTH = dimensions.width - PADDING * 2;
  const CHART_HEIGHT = dimensions.height - PADDING * 2;

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setDimensions({
          width: Math.max(width, 600),
          height: 450,
        });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const chartData = useMemo(() => {
    if (!Array.isArray(data) || !data.length) return { series: [], dates: [] };

    const filteredData = data.filter((item) =>
      location ? item.name.trim() === location : true,
    );

    const allDates = [
      ...new Set(
        filteredData.flatMap((loc) => [
          ...(loc.graph?.map((entry) => entry.ins_date) || []),
          ...(loc.graph_accepted?.map((entry) => entry.ins_date) || []),
          ...(loc.graph_rejected?.map((entry) => entry.ins_date) || []),
        ]),
      ),
    ].sort((a, b) => new Date(a) - new Date(b));

    const series = [
      ...filteredData.map((loc) => ({
        name: `${loc.name.trim()} Total`,
        color: "#6366F1",
        data: allDates.map((date) => {
          const entry = loc.graph?.find((item) => item.ins_date === date);
          return entry ? entry.count : 0;
        }),
      })),
      ...filteredData.map((loc) => ({
        name: `${loc.name.trim()} Accepted`,
        color: "#10B981",
        data: allDates.map((date) => {
          const entry = loc.graph_accepted?.find(
            (item) => item.ins_date === date,
          );
          return entry ? entry.count : 0;
        }),
      })),
      ...filteredData.map((loc) => ({
        name: `${loc.name.trim()} Rejected`,
        color: "#F43F5E",
        data: allDates.map((date) => {
          const entry = loc.graph_rejected?.find(
            (item) => item.ins_date === date,
          );
          return entry ? entry.count : 0;
        }),
      })),
    ];

    return { series, dates: allDates };
  }, [data, location]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const { xScale, yScale, gridValues } = useMemo(() => {
    const allValues = chartData.series.flatMap((s) => s.data);

    const maxY = allValues.length > 0 ? Math.max(...allValues) : 5;
    const minY = allValues.length > 0 ? Math.min(...allValues) : 0;

    const roundUpToRoughValue = (value) => {
      if (value <= 10) return value;
      return Math.ceil(value / 10) * 10;
    };

    const adjustedMaxY = roundUpToRoughValue(maxY);

    const gridStep = adjustedMaxY <= 10 ? 1 : Math.ceil(adjustedMaxY / 5);
    const numGridLines = Math.ceil(adjustedMaxY / gridStep);

    const gridValues = Array.from(
      { length: Math.max(5, numGridLines + 1) },
      (_, i) => i * gridStep,
    ).map((value) => Math.round(value));

    const xScale = (index) =>
      PADDING + (index * CHART_WIDTH) / Math.max(chartData.dates.length - 1, 1);

    const yScale = (value) => {
      const range = adjustedMaxY - minY;
      if (range === 0) return dimensions.height - PADDING;

      const normalizedValue = (value - minY) / range;
      return dimensions.height - (PADDING + normalizedValue * CHART_HEIGHT);
    };

    return { xScale, yScale, gridValues };
  }, [chartData, dimensions, CHART_WIDTH, CHART_HEIGHT]);

  const createPath = (data) => {
    if (!data.length) return "";

    const points = data.map((value, index) => ({
      x: xScale(index),
      y: yScale(value),
    }));

    const path = points.reduce((acc, point, index) => {
      if (index === 0) return `M ${point.x},${point.y}`;

      const prevPoint = points[index - 1];
      const controlPoint1 = {
        x: prevPoint.x + (point.x - prevPoint.x) / 2,
        y: prevPoint.y,
      };
      const controlPoint2 = {
        x: prevPoint.x + (point.x - prevPoint.x) / 2,
        y: point.y,
      };
      return `${acc} C ${controlPoint1.x},${controlPoint1.y} ${controlPoint2.x},${controlPoint2.y} ${point.x},${point.y}`;
    }, "");
    return path;
  };

  const [tooltip, setTooltip] = useState(null);

  const getTooltipPosition = (x, y) => {
    if (!containerRef.current) return { x, y };

    const tooltipWidth = 150;
    const tooltipHeight = 100;
    const minX = PADDING;
    const maxX = dimensions.width - PADDING - tooltipWidth;
    const minY = PADDING;
    const maxY = dimensions.height - PADDING - tooltipHeight;

    // Adjust x position
    let adjustedX = x;
    if (x + tooltipWidth > maxX) {
      adjustedX = x - tooltipWidth - 20;
    }
    if (adjustedX < minX) {
      adjustedX = minX;
    }

    // Adjust y position
    let adjustedY = y;
    if (y + tooltipHeight > maxY) {
      adjustedY = y - tooltipHeight - 10;
    }
    if (adjustedY < minY) {
      adjustedY = minY;
    }
    return { x: adjustedX, y: adjustedY };
  };

  return (
    <div ref={containerRef} className="w-full overflow-x-auto relative">
      <svg
        width="100%"
        className="font-sans"
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="xMinYMin meet"
      >
        <line
          x1={PADDING}
          y1={PADDING}
          x2={PADDING}
          y2={dimensions.height - PADDING}
          stroke="#CBD5E1"
          strokeWidth="1"
        />

        <line
          x1={PADDING}
          stroke="#CBD5E1"
          strokeWidth="1"
          y1={dimensions.height - PADDING}
          x2={dimensions.width - PADDING}
          y2={dimensions.height - PADDING}
        />

        <text
          className="text-xs fill-slate-500"
          x={20}
          y={dimensions.height / 2}
          transform={`rotate(-90, 20, ${dimensions.height / 2})`}
        >
          {/* Weld Counts */}
          {inspectionProject === 295 ? "Weld Counts" : "Inspection Counts"}
        </text>

        {gridValues.map((tick) => (
          <g key={tick}>
            <line
              x1={PADDING}
              y1={yScale(tick)}
              x2={dimensions.width - PADDING}
              y2={yScale(tick)}
              stroke="#E2E8F0"
              strokeWidth="1"
            />
            <text
              className="text-xs fill-slate-500"
              x={PADDING - 10}
              y={yScale(tick)}
              textAnchor="end"
              alignmentBaseline="middle"
            >
              {Math.round(tick)}
            </text>
          </g>
        ))}

        {chartData.dates.map((date, index) => (
          <text
            className="text-xs fill-slate-500"
            key={date}
            x={xScale(index)}
            textAnchor="end"
            y={dimensions.height - PADDING + 20}
            transform={`rotate(-38, ${xScale(index)}, ${
              dimensions.height - PADDING + 20
            })`}
          >
            {formatDate(date)}
          </text>
        ))}

        {chartData.series.map((series) => (
          <g key={series.name}>
            <path
              d={createPath(series.data)}
              fill="none"
              stroke={series.color}
              strokeWidth="3"
            />
            {series.data.map((value, index) => {
              const x = xScale(index);
              const y = yScale(value);
              if (isNaN(x) || isNaN(y)) return null;
              return (
                <circle
                  key={`${series.name}-${index}`}
                  cx={x}
                  cy={y}
                  r="5"
                  fill={series.color}
                  onMouseEnter={() => {
                    const { x: tooltipX, y: tooltipY } = getTooltipPosition(
                      x,
                      y,
                    );
                    setTooltip({
                      x: tooltipX,
                      y: tooltipY,
                      value,
                      date: formatDate(chartData.dates[index]),
                      name: series.name,
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                  className="cursor-pointer"
                />
              );
            })}
          </g>
        ))}
      </svg>

      {tooltip && (
        <div
          className={`absolute bg-white p-4 rounded-xl shadow-sm border text-sm ${
            tooltip.name?.toLowerCase().includes("total")
              ? "border-blue-700"
              : tooltip.name?.toLowerCase().includes("accepted")
                ? "border-emerald-700"
                : tooltip.name?.toLowerCase().includes("rejected")
                  ? "border-amber-700"
                  : "bg-white border-slate-300"
          }`}
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            zIndex: 1000,
            minWidth: "150px",
            pointerEvents: "none",
          }}
        >
          <div className="mb-2">
            <div className="bg-slate-100 text-slate-600 py-1 px-2 rounded-md text-xs font-medium">
              {tooltip.date}
            </div>
            <div className="border-t border-slate-300 mt-2"></div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500 mr-2">{tooltip.name}</span>
            <span className="text-slate-800 font-semibold">
              {Math.round(tooltip.value)}
            </span>
          </div>
          <div
            className={`absolute -top-[6px] left-1/2 transform -translate-x-1/2 w-3 h-3 border rotate-45 ${
              tooltip.name?.toLowerCase().includes("total")
                ? "bg-blue-700 border-blue-700"
                : tooltip.name?.toLowerCase().includes("accepted")
                  ? "bg-emerald-700 border-emerald-700"
                  : tooltip.name?.toLowerCase().includes("rejected")
                    ? "bg-amber-700 border-amber-700"
                    : "bg-white border-slate-300"
            }`}
          ></div>
        </div>
      )}

      <div className="flex justify-center gap-4 mt-4">
        {chartData.series.map((series) => (
          <div key={series.name} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: series.color }}
            />
            <span className="text-sm text-slate-600 font-medium">
              {series.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
export default ApexChart;
